import React, { useState, useEffect, useMemo } from 'react'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import ErrorPlaceholder from 'components_new/molecules/ErrorPlaceholder'
import CheckboxItem from 'components_new/molecules/CheckboxItem'
import GenericListItem from 'components_new/molecules/GenericListItem'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import OntologyLayout, {
  PLACEHOLDER_HEIGHT
} from 'components_new/templates/internal/Ontology'
import ImportSection from 'components_new/templates/internal/Ontology/ImportSection'

import { useSIMActions } from 'redux/actions/DataPlatform/SIM'
import { useStandardModelActions } from 'redux/actions/DataPlatform/StandardModels'
import { useSourceSchemaActions } from 'redux/actions/DataPlatform/SourceSchemasV2'
import {
  SIMSchemaField,
  SIMSchemaModel
} from 'redux/api/DataPlatform/dataProducts/v4'
import {
  useSIMFieldSources,
  useSIMWithSources,
  useSIMStatus
} from 'redux/hooks/SIM'
import {
  useShouldFetchSourceSchema,
  useSourcesFetched
} from 'redux/hooks/SimTables'
import { SIMFieldSources } from 'redux/selectors/SIM'
import { useTestEnvironment } from 'redux/hooks/TestEnvironment'
import { DATA_TYPES } from 'helpers/Constants'
import { Color } from 'themes'

const SOURCE_COLORS: Color[] = [
  'primary',
  'secondary',
  'tertiary',
  'quaternary',
  'quintary',
  'senary',
  'septenary',
  'octonary',
  'nonary',
  'denary'
]

const getSourceColor = (source: string): Color => {
  const hash = source
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0)

  return SOURCE_COLORS[hash % SOURCE_COLORS.length]
}

/**
 * Sheet order and tab labels. The model carries the `category` key; the label
 * and the order it reads in are presentation, and live here.
 */
const CATEGORY_SHEETS: { key: string; label: string }[] = [
  { key: 'base', label: 'Grunddata' },
  { key: 'leasing', label: 'Uthyrning' },
  { key: 'market', label: 'Marknad' },
  { key: 'economical', label: 'Ekonomi' },
  { key: 'maintenance', label: 'Underhåll' },
  { key: 'energy', label: 'Energi' },
  { key: 'sustainability', label: 'Hållbarhet' }
]

const UNCATEGORISED_LABEL = 'Övrigt'

/**
 * Families of models that are identical in shape and only differ by level.
 * Listing six two-field area tables separately is noise, so they collapse
 * into one section built from the first member's fields.
 */
const COLLAPSED_GROUPS: {
  technicalNames: string[]
  label: string
  description: string
}[] = [
  {
    technicalNames: [
      'result_forecast_one',
      'result_forecast_two',
      'result_forecast_three'
    ],
    label: 'Prognos 1–3',
    description:
      'Ekonomiska prognoser. Det finns tre oberoende prognosversioner med '
      + 'identisk struktur – leverera de ni faktiskt använder, till exempel '
      + 'en per prognostillfälle under året. Fälten nedan gäller alla tre.'
  },
  {
    technicalNames: [
      'area_one',
      'area_two',
      'area_three',
      'area_four',
      'area_five',
      'area_six'
    ],
    label: 'Områden och avdelningar',
    description:
      'Fritt definierade indelningsnivåer ovanför fastighet – geografiskt '
      + 'som distrikt, orter eller stadsdelar, eller organisatoriskt som '
      + 'avdelningar, förvaltningsområden eller resultatenheter. Vi behöver '
      + 'bara ett ID och ett namn per område. Det finns sex oberoende nivåer '
      + 'att använda: de behöver inte bilda en hierarki, och ni använder bara '
      + 'de nivåer som motsvarar er egen indelning.'
  }
]

// The customer's own free dimensions, and they carry no description text.
const SKIPPED_SECTIONS = ['customer_specific']

/**
 * Which fields the customer has already modelled: the real_names with a
 * mapped MAP column, keyed by table technical name. Built from the ontology
 * overview, so it is only as fresh as the loaded SIM.
 */
type ModelledLookup = Map<string, Set<string>>

// Collapsed fields and groups remember their members, so the modelled status
// can be resolved against every original rather than the representative one.
type ExportField = SIMSchemaField & { real_names?: string[] }
type ExportModel = SIMSchemaModel & {
  fields: ExportField[]
  technical_names?: string[]
}

/**
 * Field families that are identical in shape and only differ by level. Six
 * near-identical area foreign keys read as noise, so they collapse into one
 * row that just states the relation exists. The collapsed row keeps the
 * strongest requirement among its members.
 */
const COLLAPSED_FIELDS: {
  realNames: string[]
  label: string
  description: string
}[] = [
  {
    realNames: [
      'area_one_id',
      'area_two_id',
      'area_three_id',
      'area_four_id',
      'area_five_id',
      'area_six_id'
    ],
    label: 'Område 1–6 ID',
    description:
      'Koppling till er egen områdes- eller avdelningsindelning – en '
      + 'referens per nivå ni använder, upp till sex. Se tabellen Områden '
      + 'och avdelningar.'
  }
]

// Swedish labels only — the technical names are ours to map, not the
// customer's to fill in.
type Column = { header: string; width: number; wrap?: boolean }

const DESCRIPTION_COLUMN: Column = {
  header: 'Beskrivning',
  width: 104,
  wrap: true
}

// Opt-in: tells the customer what is already delivered, so the sheet doubles
// as a gap list rather than a full requirement.
const MODELLED_COLUMN: Column = { header: 'Modellerad', width: 14 }

// Opt-in: which source columns each modelled field is built from.
const SOURCE_COLUMN: Column = { header: 'Källa', width: 48, wrap: true }

const columnsFor = (
  includeModelled: boolean,
  includeSources: boolean
): Column[] => [
  { header: 'Fält', width: 34 },
  { header: 'Typ', width: 14 },
  { header: 'Krav', width: 16 },
  ...(includeModelled ? [MODELLED_COLUMN] : []),
  ...(includeSources ? [SOURCE_COLUMN] : []),
  DESCRIPTION_COLUMN
]

const REQUIREMENT_COLUMN = 3
const MODELLED_COLUMN_INDEX = 4

/**
 * Presentation of the model's `requirement` key. Rank drives the row order so
 * the fields we actually need are read first; the label is what the customer
 * sees. Anything unclassified falls in with 'Bör', which is what an unset
 * value means.
 */
const REQUIREMENTS: {
  [key: string]: { label: string; rank: number; muted: boolean }
} = {
  required: { label: 'Obligatoriskt', rank: 0, muted: false },
  recommended: { label: 'Bör', rank: 1, muted: false },
  optional: { label: 'Valfritt', rank: 2, muted: true }
}

const DEFAULT_REQUIREMENT = 'recommended'

const requirementOf = (field: SIMSchemaField) =>
  REQUIREMENTS[field.requirement || DEFAULT_REQUIREMENT]
  || REQUIREMENTS[DEFAULT_REQUIREMENT]

const BLACK = 'FF000000'
const WHITE = 'FFFFFFFF'
const GREEN = 'FFBBF7D0'
const RULE = 'FFE5E7EB'
const MUTED = 'FF6B7280'

const solid = (argb: string) =>
  ({ type: 'pattern', pattern: 'solid', fgColor: { argb } } as const)

const collapseFields = (fields: SIMSchemaField[]): ExportField[] => {
  let out: ExportField[] = fields

  COLLAPSED_FIELDS.forEach(({ realNames, label, description }) => {
    const members = out.filter((field) => realNames.includes(field.real_name))

    if (members.length === 0) {
      return
    }

    const strongest = members
      .slice()
      .sort((a, b) => requirementOf(a).rank - requirementOf(b).rank)[0]

    out = [
      ...out.filter((field) => !realNames.includes(field.real_name)),
      { ...strongest, name: label, description, real_names: realNames }
    ]
  })

  return out
}

const requirementFields = (model: SIMSchemaModel) =>
  collapseFields(
    model.fields.filter(
      (field) =>
        !field.exclude_from_requirement
        && (!field.section || !SKIPPED_SECTIONS.includes(field.section))
    )
  )

/**
 * Replaces each collapsed family with a single synthetic model. The group
 * keeps the category of its first present member, so it sheets where the
 * originals would have.
 */
const collapseGroups = (models: SIMSchemaModel[]): ExportModel[] => {
  const grouped: ExportModel[] = [...models]

  COLLAPSED_GROUPS.forEach(({ technicalNames, label, description }) => {
    const members = technicalNames
      .map((name) => grouped.find((model) => model.technical_name === name))
      .filter((model): model is SIMSchemaModel => Boolean(model))

    if (members.length === 0) {
      return
    }

    members.forEach((member) => grouped.splice(grouped.indexOf(member), 1))

    grouped.push({
      ...members[0],
      name: label,
      plural: label,
      description,
      technical_names: members.map((member) => member.technical_name)
    })
  })

  return grouped
}

// The originals behind a collapsed model or field.
const memberTables = (model: ExportModel) =>
  model.technical_names ?? [model.technical_name]

const memberNames = (field: ExportField) =>
  field.real_names ?? [field.real_name]

/**
 * A field is modelled when any member table of the (possibly collapsed) model
 * has a mapped MAP column for any of the field's (possibly collapsed) names.
 */
const isModelled = (
  lookup: ModelledLookup,
  model: ExportModel,
  field: ExportField
): boolean =>
  memberTables(model).some((table) =>
    memberNames(field).some((name) => lookup.get(table)?.has(name) ?? false))

// Distinct lines, for a cell that merges several collapsed members.
const lines = (values: string[]) => Array.from(new Set(values)).join('\n')

const fieldSources = (
  lookup: SIMFieldSources,
  model: ExportModel,
  field: ExportField
): string =>
  lines(
    memberTables(model).flatMap((table) =>
      memberNames(field).flatMap(
        (name) => lookup.get(table)?.fields[name] ?? []
      ))
  )

// For the banner row.
const modelSources = (lookup: SIMFieldSources, model: ExportModel): string =>
  lines(
    memberTables(model).flatMap((table) => lookup.get(table)?.tables ?? [])
  )

const sheetModels = (
  models: ExportModel[],
  key: string,
  label: string,
  known: string[]
) =>
  models
    .filter((model) =>
      (label === UNCATEGORISED_LABEL
        ? !model.category || !known.includes(model.category)
        : model.category === key))
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))

/**
 * Excel does not auto-fit a row whose height is set, and the banner carries
 * the table description — so approximate the wrapped line count from the
 * description length against the Beskrivning column width.
 */
const bannerHeight = (description?: string): number => {
  if (!description) {
    return 22
  }

  const charsPerLine = DESCRIPTION_COLUMN.width
  const lines = Math.ceil(description.length / charsPerLine)

  return Math.max(22, lines * 15 + 8)
}

/**
 * Build the technical requirement as an .xlsx with one sheet per model
 * category and trigger a download. Models flagged `exclude_from_requirement`
 * are left out entirely; anything without a known category lands on a
 * trailing sheet, so a newly added model can never silently vanish from the
 * export.
 *
 * Layout: each table gets a green banner row carrying its name and purpose,
 * then its fields underneath. That beats repeating the table name on every
 * row, which is what made the old flat CSV hard to read.
 *
 * The Valfritt column is deliberately blank rather than 'Obligatoriskt' for
 * unflagged fields: only part of the model is classified, so calling the rest
 * mandatory would overstate what we know.
 *
 * With `modelled` set, a Modellerad column marks each field Ja/Nej against the
 * customer's current SIM and the banner carries the per-table tally.
 *
 * With `sources` set, Källa lists the source columns each modelled field is
 * built from, and the banner lists the table's source tables.
 */
const exportStandardModelAsXlsx = async (
  schema: SIMSchemaModel[],
  selected: string[],
  modelled: ModelledLookup | null,
  sources: SIMFieldSources | null
): Promise<void> => {
  const included = collapseGroups(
    schema.filter((model) => !model.exclude_from_requirement)
  )
  const known = CATEGORY_SHEETS.map(({ key }) => key)
  const columns = columnsFor(modelled !== null, sources !== null)
  const wraps = (column: number) => Boolean(columns[column - 1].wrap)

  const workbook = new ExcelJS.Workbook()

  // Övrigt is not selectable: it only ever holds models missing a category,
  // which is a bug we want visible rather than something to opt out of.
  const sheets = [
    ...CATEGORY_SHEETS.filter(({ key }) => selected.includes(key)),
    { key: UNCATEGORISED_LABEL, label: UNCATEGORISED_LABEL }
  ]

  sheets.forEach(({ key, label }) => {
    const models = sheetModels(included, key, label, known)

    if (models.length === 0) {
      return
    }

    const worksheet = workbook.addWorksheet(label)

    worksheet.views = [{ state: 'frozen', ySplit: 1, showGridLines: false }]
    columns.forEach(({ width }, index) => {
      worksheet.getColumn(index + 1).width = width
    })

    const headerRow = worksheet.addRow(columns.map(({ header }) => header))

    headerRow.height = 22
    headerRow.eachCell((cell) => {
      cell.fill = solid(BLACK)
      cell.font = { bold: true, color: { argb: WHITE }, size: 11 }
      cell.alignment = { vertical: 'middle' }
    })

    models.forEach((model) => {
      const fields = requirementFields(model)
        .slice()
        // Required first, optional last, alphabetical within each level.
        .sort(
          (a, b) =>
            requirementOf(a).rank - requirementOf(b).rank
            || a.name.localeCompare(b.name)
        )
        .map((field) => ({
          field,
          modelled: modelled ? isModelled(modelled, model, field) : false
        }))

      const modelledCount = fields.filter((item) => item.modelled).length

      const banner = worksheet.addRow([
        model.plural || model.name,
        '',
        '',
        ...(modelled ? [`${modelledCount} av ${fields.length}`] : []),
        ...(sources ? [modelSources(sources, model)] : []),
        model.description || ''
      ])

      banner.height = bannerHeight(model.description)
      banner.eachCell({ includeEmpty: true }, (cell, column) => {
        cell.fill = solid(GREEN)
        cell.alignment = {
          vertical: 'middle',
          wrapText: wraps(column),
          horizontal:
            modelled && column === MODELLED_COLUMN_INDEX ? 'center' : 'left'
        }
        cell.font = column === 1 ? { bold: true, size: 12 } : { size: 10 }
      })

      fields.forEach(({ field, modelled: isFieldModelled }) => {
        const requirement = requirementOf(field)
        const row = worksheet.addRow([
          field.name,
          DATA_TYPES[field.type] || '',
          requirement.label,
          ...(modelled ? [isFieldModelled ? 'Ja' : 'Nej'] : []),
          ...(sources ? [fieldSources(sources, model, field)] : []),
          field.description || ''
        ])

        row.eachCell({ includeEmpty: true }, (cell, column) => {
          const isModelledColumn = modelled && column === MODELLED_COLUMN_INDEX
          const centered = column === REQUIREMENT_COLUMN || isModelledColumn

          cell.alignment = {
            vertical: 'top',
            wrapText: wraps(column),
            horizontal: centered ? 'center' : 'left'
          }
          cell.border = { bottom: { style: 'thin', color: { argb: RULE } } }

          if (column === REQUIREMENT_COLUMN) {
            cell.font = {
              size: 10,
              bold: requirement.rank === 0,
              color: { argb: requirement.muted ? MUTED : BLACK }
            }
          }

          if (isModelledColumn) {
            cell.font = {
              size: 10,
              bold: isFieldModelled,
              color: { argb: isFieldModelled ? BLACK : MUTED }
            }
          }
        })
      })

      worksheet.addRow([])
    })
  })

  const buffer = await workbook.xlsx.writeBuffer()

  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }),
    'Standardmodell.xlsx'
  )
}

const OntologyStandard = () => {
  const { tryGetSIM, tryGetSIMSchema, tryStartSIMImport, tryPollSIMImport } =
    useSIMActions()
  const { tryActivateStandardModel } = useStandardModelActions()

  const sim = useSIMWithSources()
  const fieldSourcesLookup = useSIMFieldSources()
  const simStatus = useSIMStatus()

  const { variant } = useTestEnvironment()

  const { tryGetSourceSchemas } = useSourceSchemaActions()
  const shouldFetchSourceSchemas = useShouldFetchSourceSchema()
  const sourcesFetched = useSourcesFetched()

  const [filterType, setFilterType] = useState('all')
  const [search, setSearch] = useState('')
  const [exporting, setExporting] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [schema, setSchema] = useState<SIMSchemaModel[] | null>(null)
  const [loadingSchema, setLoadingSchema] = useState(false)
  const [modules, setModules] = useState<string[]>(() =>
    CATEGORY_SHEETS.map(({ key }) => key))
  const [includeModelled, setIncludeModelled] = useState(false)
  const [includeSources, setIncludeSources] = useState(false)
  const [activating, setActivating] = useState(false)
  const [pollingIntervalId, setPollingIntervalId] = useState<ReturnType<
    typeof setInterval
  > | null>(null)

  // The schema is fetched when the dialog opens rather than on export, so the
  // module list can show how many tables each one actually contributes.
  const handleOpenExport = async () => {
    setExportOpen(true)

    if (schema) {
      return
    }

    setLoadingSchema(true)

    try {
      setSchema(await tryGetSIMSchema())
    } finally {
      setLoadingSchema(false)
    }
  }

  const handleExport = async () => {
    setExporting(true)

    try {
      const data = schema ?? (await tryGetSIMSchema())

      await exportStandardModelAsXlsx(
        data,
        modules,
        includeModelled ? modelledLookup : null,
        includeSources ? fieldSourcesLookup : null
      )

      setExportOpen(false)
    } finally {
      setExporting(false)
    }
  }

  const includedModels = useMemo(
    () =>
      (schema
        ? collapseGroups(
          schema.filter((model) => !model.exclude_from_requirement)
        )
        : []),
    [schema]
  )

  const allModules = CATEGORY_SHEETS.map(({ key }) => key)

  const modelledLookup = useMemo<ModelledLookup>(
    () =>
      new Map(
        (sim?.tables ?? []).map((table) => [
          table.technical_name,
          new Set(table.modelled_fields ?? [])
        ])
      ),
    [sim]
  )

  const handleActivate = async () => {
    setActivating(true)

    try {
      await tryActivateStandardModel()
    } finally {
      setActivating(false)
    }
  }

  useEffect(() => {
    // The custom and standard overviews share this slice; refetch whenever the
    // loaded ontology isn't the standard one (first load, or returning here
    // after viewing the custom overview).
    if (sim?.type !== 'STANDARD_ONTOLOGY') {
      tryGetSIM('standard')
    }
  }, [])

  useEffect(() => {
    if (shouldFetchSourceSchemas) {
      tryGetSourceSchemas()
    }
  }, [shouldFetchSourceSchemas])

  const isFetched = simStatus === 'success' && !!sourcesFetched

  const isTestEnvironment = variant === 'test'

  const emptyContent = useMemo(() => {
    if (simStatus === 'error') {
      return <ErrorPlaceholder code={500} sx={{ height: PLACEHOLDER_HEIGHT }} />
    }

    if (simStatus === 'notFound') {
      if (isTestEnvironment) {
        return (
          <EmptyPlaceholder
            background={true}
            height={PLACEHOLDER_HEIGHT}
            icon="AccountTreeOutlined"
            size="medium"
            title="Ingen standardmodell aktiverad i testmiljön"
            body="Aktivera testmiljöns standardmodell under Våra kunder."
          />
        )
      }

      return (
        <EmptyPlaceholder
          background={true}
          height={PLACEHOLDER_HEIGHT}
          icon="AccountTreeOutlined"
          size="medium"
          title="Ingen standardmodell aktiverad"
          body="Aktivera standardmodellen för att skapa kundens tabeller."
          actions={
            <Button loading={activating} onClick={handleActivate}>
              Aktivera standardmodell
            </Button>
          }
        />
      )
    }

    if (isFetched && sim && sim.tables.length === 0) {
      return (
        <EmptyPlaceholder
          background={true}
          height={PLACEHOLDER_HEIGHT}
          icon="AccountTreeOutlined"
          size="medium"
          title="Informationsmodellen saknar tabeller"
        />
      )
    }

    return undefined
  }, [simStatus, isTestEnvironment, activating, isFetched, sim])

  const tables = useMemo(() => {
    if (!isFetched || !sim) {
      return []
    }

    return sim.tables
      .filter((table) => {
        let toReturn = true

        if (filterType === 'modeled') {
          toReturn = table.source_tables.length > 0
        } else if (filterType === 'unmodeled') {
          toReturn = table.source_tables.length === 0
        }

        if (search) {
          const displayName = table.alias ?? table.name

          toReturn =
            toReturn && displayName.toLowerCase().includes(search.toLowerCase())
        }

        return toReturn
      })
      .map((table) => (
        <GenericListItem
          action="to"
          key={table.id}
          size="small"
          flexTitle
          title={table.name}
          body={table.alias ? table.name : undefined}
          titleAdornment={
            table.source_names.length > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {table.source_names.map((source) => (
                  <Chip
                    key={source}
                    size={'xsmall'}
                    sx={{ ml: 1 }}
                    label={source}
                    color={getSourceColor(source)}
                  />
                ))}
              </Box>
            ) : undefined
          }
          to={`/data-platform/standard/tables/${table.id}`}
          variant="entity"
        />
      ))
  }, [isFetched, filterType, search, sim])

  return (
    <OntologyLayout
      currentType="standard"
      isFetched={isFetched}
      search={search}
      onSearchChange={setSearch}
      filterType={filterType}
      onFilterTypeChange={setFilterType}
      topContent={
        sim ? (
          <ImportSection
            id={sim.id}
            latestRun={sim.latest_run}
            onStart={(id) => tryStartSIMImport(id)}
            onPoll={(id) => tryPollSIMImport(id)}
            pollingIntervalId={pollingIntervalId}
            setPollingIntervalId={setPollingIntervalId}
          />
        ) : undefined
      }
      filterActions={
        <Button
          onClick={handleOpenExport}
          startIcon={<Icon name="Download" />}
          variant="text"
        >
          Exportera som .xlsx
        </Button>
      }
      emptyContent={emptyContent}
    >
      <>
        {tables}
        <SimpleDialog
          actions={
            <>
              <Button onClick={() => setExportOpen(false)} variant="text">
              Avbryt
              </Button>
              <Button
                disabled={modules.length === 0}
                loading={exporting}
                onClick={handleExport}
                startIcon={<Icon name="Download" />}
              >
              Exportera
              </Button>
            </>
          }
          contentText="Varje modul blir ett eget blad i filen. Välj bort de områden kunden inte ska leverera."
          hasClose={true}
          maxWidth="xs"
          onClose={() => setExportOpen(false)}
          open={exportOpen}
          title="Exportera tekniskt underlag"
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {CATEGORY_SHEETS.map(({ key, label }) => {
              const checked = modules.includes(key)
              const tables = loadingSchema
                ? null
                : sheetModels(includedModels, key, label, allModules).length

              return (
                <CheckboxItem
                  checked={checked}
                  fullWidth={true}
                  hover={true}
                  key={key}
                  label={
                    <Box
                      component="span"
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'space-between'
                      }}
                    >
                      <span>{label}</span>
                      <Text color="text.secondary" component="span" variant="body2">
                        {tables === null ? '…' : `${tables} tabeller`}
                      </Text>
                    </Box>
                  }
                  onChange={() =>
                    setModules((current) =>
                      (checked
                        ? current.filter((item) => item !== key)
                        : [...current, key]))}
                  size="small"
                />
              )
            })}
          </Box>
          <Button
            onClick={() =>
              setModules(
                modules.length === allModules.length ? [] : allModules
              )}
            size="small"
            sx={{ mt: 1 }}
            variant="text"
          >
            {modules.length === allModules.length
              ? 'Avmarkera alla'
              : 'Markera alla'}
          </Button>
          <Divider sx={{ my: 2 }} />
          <CheckboxItem
            checked={includeModelled}
            disabled={!sim}
            fullWidth={true}
            hover={true}
            label="Inkludera vilka fält som redan är modellerade"
            onChange={() => setIncludeModelled((current) => !current)}
            size="small"
          />
          <CheckboxItem
            checked={includeSources}
            disabled={!sim}
            fullWidth={true}
            hover={true}
            label="Inkludera källa per fält"
            onChange={() => setIncludeSources((current) => !current)}
            size="small"
          />
        </SimpleDialog>
      </>
    </OntologyLayout>
  )
}

export default OntologyStandard
